import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import {
  BarChart3, TrendingUp, TrendingDown, CheckCircle,
  XCircle, Clock, FileCode, Play, Calendar,
  Activity, Target, Zap, AlertTriangle
} from "lucide-react";
import { AppNav } from "@/components/AppNav";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Analytics() {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState("30");

  useEffect(() => {
    fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRange]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/analytics/dashboard?days=${timeRange}`, { withCredentials: true });
      setAnalytics(response.data);
    } catch (error) {
      console.error("Failed to fetch analytics:", error);
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-400';
      case 'failed': return 'text-red-400';
      case 'running': return 'text-amber-400';
      default: return 'text-slate-400';
    }
  };

  const formatDuration = (ms) => {
    if (!ms) return "N/A";
    const seconds = Math.round(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  return (
    <div className="min-h-screen bg-[#f8f9fb] text-slate-900" data-testid="analytics-page">
      <div className="fixed inset-0 hero-glow" />
      <div className="fixed inset-0 opacity-5" 
        style={{
          backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"-%3E%3Cg fill="none" fill-rule="evenodd"-%3E%3Cg fill="%23ffffff" fill-opacity="0.4"-%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'
        }}
      />

      <div className="relative z-10">
        <AppNav />

        {/* Main Content */}
        <div className="px-6 py-12 md:px-12">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-12">
              <div>
                <h1 className="text-4xl md:text-5xl font-extrabold tracking-tighter mb-4" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                  Analytics
                </h1>
                <p className="text-slate-400">Test execution insights and trends</p>
              </div>
              <Select value={timeRange} onValueChange={setTimeRange}>
                <SelectTrigger data-testid="time-range-select" className="w-[180px] bg-[#0B0F1A] border-white/10 text-white rounded-sm">
                  <Calendar className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0B0F1A] border-white/10">
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="14">Last 14 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-24">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00E599]"></div>
              </div>
            ) : analytics ? (
              <>
                {/* Overview Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <Card className="glass-card border-white/5 rounded-xl">
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between mb-4">
                          <div className="w-12 h-12 bg-[#00E599]/10 rounded-sm flex items-center justify-center">
                            <FileCode className="w-6 h-6 text-[#00E599]" />
                          </div>
                          <Badge className="bg-slate-800 text-slate-300 border-slate-700">Total</Badge>
                        </div>
                        <p className="text-3xl font-bold mb-1" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                          {analytics.summary?.total_test_cases || 0}
                        </p>
                        <p className="text-sm text-slate-400">Test Cases</p>
                      </CardContent>
                    </Card>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                  >
                    <Card className="glass-card border-white/5 rounded-xl">
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between mb-4">
                          <div className="w-12 h-12 bg-[#D946EF]/10 rounded-sm flex items-center justify-center">
                            <Play className="w-6 h-6 text-[#D946EF]" />
                          </div>
                          <Badge className="bg-slate-800 text-slate-300 border-slate-700">Period</Badge>
                        </div>
                        <p className="text-3xl font-bold mb-1" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                          {analytics.summary?.total_executions || 0}
                        </p>
                        <p className="text-sm text-slate-400">Executions</p>
                      </CardContent>
                    </Card>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                  >
                    <Card className="glass-card border-white/5 rounded-xl">
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between mb-4">
                          <div className="w-12 h-12 bg-green-500/10 rounded-sm flex items-center justify-center">
                            <Target className="w-6 h-6 text-green-400" />
                          </div>
                          {analytics.summary?.pass_rate_change > 0 ? (
                            <Badge className="bg-green-500/10 text-green-400 border-green-500/20">
                              <TrendingUp className="w-3 h-3 mr-1" />
                              +{analytics.summary.pass_rate_change}%
                            </Badge>
                          ) : analytics.summary?.pass_rate_change < 0 ? (
                            <Badge className="bg-red-500/10 text-red-400 border-red-500/20">
                              <TrendingDown className="w-3 h-3 mr-1" />
                              {analytics.summary.pass_rate_change}%
                            </Badge>
                          ) : (
                            <Badge className="bg-slate-800 text-slate-300 border-slate-700">Stable</Badge>
                          )}
                        </div>
                        <p className="text-3xl font-bold mb-1" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                          {analytics.summary?.pass_rate || 0}%
                        </p>
                        <p className="text-sm text-slate-400">Pass Rate</p>
                      </CardContent>
                    </Card>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                  >
                    <Card className="glass-card border-white/5 rounded-xl">
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between mb-4">
                          <div className="w-12 h-12 bg-[#06B6D4]/10 rounded-sm flex items-center justify-center">
                            <Clock className="w-6 h-6 text-[#06B6D4]" />
                          </div>
                          <Badge className="bg-slate-800 text-slate-300 border-slate-700">Avg</Badge>
                        </div>
                        <p className="text-3xl font-bold mb-1" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                          {formatDuration(analytics.summary?.avg_duration)}
                        </p>
                        <p className="text-sm text-slate-400">Execution Time</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                </div>

                {/* Status Distribution */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-12">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                  >
                    <Card className="glass-card border-white/5 rounded-xl">
                      <CardHeader>
                        <CardTitle style={{ fontFamily: 'Unbounded, sans-serif' }}>Execution Status</CardTitle>
                        <CardDescription className="text-slate-400">Distribution of test results</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-green-400" />
                              <span>Passed</span>
                            </div>
                            <span className="font-semibold">{analytics.status_breakdown?.completed || 0}</span>
                          </div>
                          <Progress 
                            value={analytics.summary?.total_executions > 0 
                              ? ((analytics.status_breakdown?.completed || 0) / analytics.summary.total_executions) * 100 
                              : 0} 
                            className="h-2 bg-slate-800" 
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <XCircle className="w-4 h-4 text-red-400" />
                              <span>Failed</span>
                            </div>
                            <span className="font-semibold">{analytics.status_breakdown?.failed || 0}</span>
                          </div>
                          <Progress 
                            value={analytics.summary?.total_executions > 0 
                              ? ((analytics.status_breakdown?.failed || 0) / analytics.summary.total_executions) * 100 
                              : 0} 
                            className="h-2 bg-slate-800 [&>div]:bg-red-400" 
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Activity className="w-4 h-4 text-amber-400" />
                              <span>Running</span>
                            </div>
                            <span className="font-semibold">{analytics.status_breakdown?.running || 0}</span>
                          </div>
                          <Progress 
                            value={analytics.summary?.total_executions > 0 
                              ? ((analytics.status_breakdown?.running || 0) / analytics.summary.total_executions) * 100 
                              : 0} 
                            className="h-2 bg-slate-800 [&>div]:bg-amber-400" 
                          />
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>

                  {/* Recent Trends */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                  >
                    <Card className="glass-card border-white/5 rounded-xl">
                      <CardHeader>
                        <CardTitle style={{ fontFamily: 'Unbounded, sans-serif' }}>Daily Executions</CardTitle>
                        <CardDescription className="text-slate-400">Last {timeRange} days activity</CardDescription>
                      </CardHeader>
                      <CardContent>
                        {analytics.daily_executions && analytics.daily_executions.length > 0 ? (
                          <div className="flex items-end gap-1 h-[200px]">
                            {analytics.daily_executions.slice(-14).map((day, idx) => {
                              const maxCount = Math.max(...analytics.daily_executions.map(d => d.count));
                              const height = maxCount > 0 ? (day.count / maxCount) * 100 : 0;
                              return (
                                <div
                                  key={idx}
                                  className="flex-1 flex flex-col items-center gap-1"
                                >
                                  <div 
                                    className="w-full bg-[#00E599]/20 hover:bg-[#00E599]/40 transition-colors rounded-t-sm"
                                    style={{ height: `${Math.max(height, 4)}%` }}
                                    title={`${day.date}: ${day.count} executions`}
                                  />
                                  <span className="text-[10px] text-slate-500 truncate w-full text-center">
                                    {new Date(day.date).getDate()}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="flex items-center justify-center h-[200px] text-slate-500">
                            No execution data for this period
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>
                </div>

                {/* Top Tests & Recent Failures */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                  >
                    <Card className="glass-card border-white/5 rounded-xl">
                      <CardHeader>
                        <CardTitle style={{ fontFamily: 'Unbounded, sans-serif' }}>Most Executed Tests</CardTitle>
                        <CardDescription className="text-slate-400">Tests run most frequently</CardDescription>
                      </CardHeader>
                      <CardContent>
                        {analytics.top_tests && analytics.top_tests.length > 0 ? (
                          <div className="space-y-3">
                            {analytics.top_tests.slice(0, 5).map((test, idx) => (
                              <div 
                                key={idx}
                                className="flex items-center justify-between p-3 bg-white/5 rounded-sm hover:bg-white/10 cursor-pointer transition-colors"
                                onClick={() => navigate(`/edit-test/${test.id}`)}
                              >
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 bg-[#00E599]/10 rounded-sm flex items-center justify-center">
                                    <span className="text-[#00E599] font-bold text-sm">{idx + 1}</span>
                                  </div>
                                  <div>
                                    <p className="font-medium truncate max-w-[200px]">{test.name}</p>
                                    <p className="text-xs text-slate-500">{test.execution_count} runs</p>
                                  </div>
                                </div>
                                <Badge className={test.pass_rate >= 80 ? 'bg-green-500/10 text-green-400 border-green-500/20' : test.pass_rate >= 50 ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' : 'bg-red-500/10 text-red-400 border-red-500/20'}>
                                  {test.pass_rate}%
                                </Badge>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-8 text-slate-500">
                            No test execution data available
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7 }}
                  >
                    <Card className="glass-card border-white/5 rounded-xl">
                      <CardHeader>
                        <CardTitle style={{ fontFamily: 'Unbounded, sans-serif' }}>Recent Failures</CardTitle>
                        <CardDescription className="text-slate-400">Tests that need attention</CardDescription>
                      </CardHeader>
                      <CardContent>
                        {analytics.recent_failures && analytics.recent_failures.length > 0 ? (
                          <div className="space-y-3">
                            {analytics.recent_failures.slice(0, 5).map((failure, idx) => (
                              <div 
                                key={idx}
                                className="flex items-center justify-between p-3 bg-red-500/5 border border-red-500/10 rounded-sm hover:bg-red-500/10 cursor-pointer transition-colors"
                                onClick={() => navigate(`/execution/${failure.execution_id}`)}
                              >
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 bg-red-500/10 rounded-sm flex items-center justify-center">
                                    <AlertTriangle className="w-4 h-4 text-red-400" />
                                  </div>
                                  <div>
                                    <p className="font-medium truncate max-w-[200px]">{failure.test_name}</p>
                                    <p className="text-xs text-slate-500">
                                      {new Date(failure.timestamp).toLocaleString()}
                                    </p>
                                  </div>
                                </div>
                                <Badge className="bg-red-500/10 text-red-400 border-red-500/20">
                                  Failed
                                </Badge>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-8 text-green-400">
                            <CheckCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                            <p>No recent failures!</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>
                </div>
              </>
            ) : (
              <Card className="glass-card border-white/5 rounded-xl">
                <CardContent className="p-12 text-center">
                  <BarChart3 className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">No analytics data</h3>
                  <p className="text-slate-400 mb-6">Start running tests to see analytics</p>
                  <Button 
                    className="bg-[#00E599] text-black hover:bg-[#00CC88] rounded-sm font-semibold"
                    onClick={() => navigate('/dashboard')}
                  >
                    Go to Dashboard
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
